import createStorage, { proxyStorage } from "./storage";

import { MbscEventcalendarView } from "@mobiscroll/react";
import { InboxLocalStorageKey } from "../../types/conversationInbox";
import uniqBy from "lodash/uniqBy";
import { withNewlyAddedPostContentTypes } from "./post-content-types";

let rawStorage: proxyStorage = window.localStorage;
let localStorageWrapper = createStorage(rawStorage);

export const setLocalStorage = (storage: proxyStorage): void => {
  rawStorage = storage;
  localStorageWrapper = createStorage(storage);
};

// GETS
export const getLocalStorageSkedApiToken = (): string | null =>
  localStorageWrapper.syncGetItem("skedApiToken");

export const getLocalStorageSelectedAccounts = (): string | null =>
  localStorageWrapper.syncGetItem(InboxLocalStorageKey.selectedAccounts);

export const getLocalStoragePostStatusKey = (): string | null =>
  localStorageWrapper.syncGetItem("postStatusKey");

export const getLocalStorageSelectedCalendarsIds = (): string | null =>
  localStorageWrapper.syncGetItem("$selectedCalendarsIds");

export const getLocalStorageLibraryFilter = (): string | null =>
  localStorageWrapper.syncGetItem("libraryFilter");

export const getLocalStorageSelectedPostContentTypes = (): string | null =>
  localStorageWrapper.syncGetItem("selectedPostContentTypes");

const SELECTED_POST_CONTENT_TYPES_VERSION_KEY =
  "selectedPostContentTypesVersion";
const SELECTED_POST_CONTENT_TYPES_VERSION = "2";

export const readLocalStorageSelectedPostContentTypes = ():
  unknown[] | null => {
  try {
    const parsed: unknown = JSON.parse(
      getLocalStorageSelectedPostContentTypes() ?? "null"
    );
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const trySyncSetItem = (key: string, value: string): boolean => {
  try {
    rawStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

export const migrateLocalStorageSelectedPostContentTypes = (): void => {
  try {
    if (
      localStorageWrapper.syncGetItem(
        SELECTED_POST_CONTENT_TYPES_VERSION_KEY
      ) === SELECTED_POST_CONTENT_TYPES_VERSION
    ) {
      return;
    }
  } catch {
    return;
  }
  const stored = readLocalStorageSelectedPostContentTypes();
  if (stored && stored.length > 0) {
    const merged = withNewlyAddedPostContentTypes(stored);
    if (
      merged !== stored &&
      !trySyncSetItem("selectedPostContentTypes", JSON.stringify(merged))
    ) {
      return;
    }
  }
  trySyncSetItem(
    SELECTED_POST_CONTENT_TYPES_VERSION_KEY,
    SELECTED_POST_CONTENT_TYPES_VERSION
  );
};

export const getLocalStoragePlannerSelectedAccount = (): string | null =>
  localStorageWrapper.syncGetItem("plannerSelectedAccount");

export const getLocalStorageIsConfirmDraftDelete = (): string | null =>
  localStorageWrapper.syncGetItem("isConfirmDraftDelete");

export const getLocalStorageIsConfirmPostCancel = (): string | null =>
  localStorageWrapper.syncGetItem("isConfirmPostCancel");

export const getLocalStorageIsConfirmPostDelete = (): string | null =>
  localStorageWrapper.syncGetItem("isConfirmPostDelete");

export const getLocalStorageMentionAutocomplete = (): string | null =>
  localStorageWrapper.syncGetItem("mentionAutocomplete");

export const getLocalStorageHomeSelectedGroupName = (): string | null =>
  localStorageWrapper.syncGetItem("homeSelectedGroupName");

export const getLocalStorageAddAccountToGroup = (): string | null =>
  localStorageWrapper.syncGetItem("addAccountToGroup");

export const getLocalStorageLiAudiences = (): string | null =>
  localStorageWrapper.syncGetItem("LiAudiences");

export const getLocalStorageMentionAutocompleteLi = (): [] | null => {
  const allAutocomplete = getLocalStorageMentionAutocomplete();

  if (!allAutocomplete) return null;

  return JSON.parse(allAutocomplete)["Li"] || null;
};

// SETS
export const setLocalStorageSelectedAccounts = (value: string): void =>
  localStorageWrapper.setItem(InboxLocalStorageKey.selectedAccounts, value);

export const setLocalStorageSkedApiToken = (value: string): void =>
  localStorageWrapper.setItem("skedApiToken", value);

export const SKED_API_TOKEN_REMOVED_EVENT = "sked-api-token-removed";

export const removeLocalStorageSkedApiToken = (): void => {
  localStorageWrapper.removeItem("skedApiToken");
  // Native storage events only notify other tabs.
  window.dispatchEvent(new Event(SKED_API_TOKEN_REMOVED_EVENT));
};

export const setLocalStoragePostStatusKey = (value: string): void =>
  localStorageWrapper.setItem("postStatusKey", value);

export const removeLocalStoragePostStatusKey = (): void =>
  localStorageWrapper.removeItem("postStatusKey");

export const setLocalStorageLibraryFilter = (value: string): void =>
  localStorageWrapper.setItem("libraryFilter", value);

export const setLocalStorageSelectedCalendarsIds = (value: string): void =>
  localStorageWrapper.setItem("$selectedCalendarsIds", value);

export const setLocalStoragePlannerSelectedAccount = (value: string): void =>
  localStorageWrapper.setItem("plannerSelectedAccount", value);

export const setLocalStorageIsConfirmDraftDelete = (value: string): void =>
  localStorageWrapper.setItem("isConfirmDraftDelete", value);

export const setLocalStorageIsConfirmPostCancel = (value: string): void =>
  localStorageWrapper.setItem("isConfirmPostCancel", value);

export const setLocalStorageIsConfirmPostDelete = (value: string): void =>
  localStorageWrapper.setItem("isConfirmPostDelete", value);

export const setLocalStorageMentionAutocomplete = (value: string): void =>
  localStorageWrapper.setItem("mentionAutocomplete", value);

export const setLocalStorageHomeSelectedGroupName = (value: string): void =>
  localStorageWrapper.setItem("homeSelectedGroupName", value);

export const setLocalStorageAddAccountToGroup = (value: string): void =>
  localStorageWrapper.setItem("addAccountToGroup", value);

export const setLocalStorageByKey = ({
  storageDataString,
  newValue,
  key,
  uniqKey,
}: {
  storageDataString: string | null;
  newValue: Record<string, unknown>;
  key?: string;
  uniqKey: string;
}): string => {
  const defaultStorageData = key ? {} : [];
  let storageData = storageDataString
    ? JSON.parse(storageDataString)
    : defaultStorageData;
  const storageDataByKey = key ? storageData[key] || [] : storageData;

  if (key) {
    storageData[key] = uniqBy([...storageDataByKey, newValue], uniqKey);
  } else {
    storageData = uniqBy([...storageDataByKey, newValue], uniqKey);
  }

  return JSON.stringify(storageData);
};

export const setLocalStorageMentionAutocompleteLi = (
  mentionAutocomplete: Record<string, unknown>
): void => {
  const storageDataString = getLocalStorageMentionAutocomplete();
  const newData = setLocalStorageByKey({
    storageDataString,
    newValue: mentionAutocomplete,
    key: "Li",
    uniqKey: "urn",
  });
  setLocalStorageMentionAutocomplete(newData);
};

export const setLocalStorageLiAudiences = (
  value: Record<string, unknown>
): void => {
  const storageDataString = getLocalStorageLiAudiences();
  const newData = setLocalStorageByKey({
    storageDataString,
    newValue: value,
    uniqKey: "id",
  });
  localStorageWrapper.setItem("LiAudiences", newData);
};

// HOOKS
export const useStorageSelectedPostContentTypes = (
  defaultValue: any[] | null
) =>
  localStorageWrapper.useStorage<any[] | null>(
    "selectedPostContentTypes",
    defaultValue
  );

export const useStorageDeletedRecentUser = (defaultValue: string[] | null) =>
  localStorageWrapper.useStorage<string[] | null>(
    "deletedRecentUser",
    defaultValue
  );

/** Shape stored under the postStatusKey in localStorage */
interface PostStatusKeyItem {
  label: string;
  value: string;
  color?: string;
}

export const useStoragePostStatusKey = <
  T extends PostStatusKeyItem[] | string[] = string[],
>(
  defaultValue: T
) => localStorageWrapper.useStorage<T>("postStatusKey", defaultValue);

/**
 * Approvals Board collapsed-column state, keyed by status `key` (status ID).
 * One localStorage entry holding a map so it survives status reordering and
 * persists across refreshes for the same user/browser.
 */
export const useStorageBoardCollapsedColumns = (
  defaultValue: Record<string, boolean> = {}
) =>
  localStorageWrapper.useStorage<Record<string, boolean>>(
    "approvalsBoardCollapsedColumns",
    defaultValue
  );

export const sanitizeBoardCollapsedColumns = (
  value: unknown,
  validStatusKeys: readonly string[]
): Record<string, boolean> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};

  const stored = value as Record<string, unknown>;
  return Object.fromEntries(
    validStatusKeys.flatMap((statusKey) =>
      typeof stored[statusKey] === "boolean"
        ? [[statusKey, stored[statusKey] as boolean]]
        : []
    )
  );
};

export const isBoardCollapsedColumnsSanitized = (
  value: unknown,
  sanitized: Record<string, boolean>
): boolean => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;

  const stored = value as Record<string, unknown>;
  const storedKeys = Object.keys(stored);
  const sanitizedKeys = Object.keys(sanitized);
  return (
    storedKeys.length === sanitizedKeys.length &&
    sanitizedKeys.every((key) => stored[key] === sanitized[key])
  );
};

export const useStorageApprovalsBoardMode = <
  T extends "upcoming" | "drafts" = "upcoming" | "drafts",
>(
  defaultValue: T
) => localStorageWrapper.useStorage<T>("approvalsBoardMode", defaultValue);

export enum AccountFilterEnum {
  accounts = "accounts",
  events = "events",
}

export const useStorageAccountFilter = (defaultValue: AccountFilterEnum) =>
  localStorageWrapper.useStorage<AccountFilterEnum>(
    "accountFilter",
    defaultValue
  );

export const useStorageIsFiltersOpen = (defaultValue: boolean) =>
  localStorageWrapper.useStorage<boolean>("isFiltersOpen", defaultValue);

export const useStorageIsDraftsOpen = (defaultValue: boolean) =>
  localStorageWrapper.useStorage<boolean>("isDraftsOpen", defaultValue);

export const useStorageIsWorkHours = (defaultValue: boolean) =>
  localStorageWrapper.useStorage<boolean>("isWorkHours", defaultValue);

export const useStorageAutoSubmitPlanner = (defaultValue: boolean) =>
  localStorageWrapper.useStorage<boolean>("autoSubmitPlanner", defaultValue);

export const useStorageCalendarView = (
  defaultValue: MbscEventcalendarView
): [
  MbscEventcalendarView,
  (newValue: MbscEventcalendarView | null) => void,
  () => void,
] =>
  localStorageWrapper.useStorage<MbscEventcalendarView>(
    "calendarView",
    defaultValue
  );

// Post form drafts (auto-save and restoration)
export const getLocalStoragePostFormDrafts = (): string | null =>
  localStorageWrapper.syncGetItem("postFormDrafts");

export const setLocalStoragePostFormDrafts = (value: string): void =>
  localStorageWrapper.setItem("postFormDrafts", value);

export const clearLocalStoragePostFormDrafts = (): void => {
  localStorageWrapper.removeItem("postFormDrafts");
};
